"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import toast, { Toaster } from "react-hot-toast";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  Code2,
  Key,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  ShieldCheck,
  Clock,
  X as XIcon,
  Plus,
  Trash2,
  Save,
  Palette,
  Send,
  Sparkles,
  Lock,
  RefreshCw,
  ImageIcon,
  Wallet,
  Smartphone,
} from "lucide-react";
import {
  fetchMyMerchant,
  requestApiAccess,
  updateMerchantSettings,
  rotateApiKey,
  revokeApiKey,
  type MyState,
  type Merchant,
  type ApiKeySummary,
} from "../../services/merchantApi";
import ConfirmModal from "./ConfirmModal";
import DomainChipInput from "./DomainChipInput";
import BrandingPreview from "./BrandingPreview";
import { WebhookSummaryCard } from "./webhooks/WebhookSummaryCard";

// ────────────────────────────────────────────────────────────────────────────
// API Settings — the "configuration" surface of the Developers section.
// Surface depends on the user's merchant status:
//   • none / rejected → request-access form
//   • pending         → status card
//   • approved        → full panel: keys, branding, domains/methods, webhook
//
// All mutations apply optimistic local updates instead of full reloads
// so the page never blanks between actions.
// ────────────────────────────────────────────────────────────────────────────

// Inlined brand marks so the method tiles show real logos without
// pulling extra image assets. `currentColor` lets us tint where needed.
function CoinbaseGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#FFFFFF" />
      <path
        d="M12 5.5a6.5 6.5 0 1 0 6.42 7.51h-3.18a3.36 3.36 0 1 1 0-2.02h3.18A6.51 6.51 0 0 0 12 5.5z"
        fill="#0052FF"
      />
    </svg>
  );
}

function CashAppGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M23.59 3.475a5.1 5.1 0 0 0-3.05-3.05c-1.31-.42-2.5-.42-4.92-.42H8.36c-2.4 0-3.61 0-4.9.4a5.1 5.1 0 0 0-3.05 3.05C0 4.765 0 5.965 0 8.365v7.27c0 2.41 0 3.6.4 4.91a5.1 5.1 0 0 0 3.05 3.05c1.3.4 2.5.4 4.9.4h7.28c2.41 0 3.61 0 4.91-.4a5.1 5.1 0 0 0 3.05-3.06c.41-1.3.41-2.5.41-4.9V8.365c0-2.4 0-3.6-.41-4.9zm-6.17 5.94l-.93.93a.5.5 0 0 1-.67.01 5 5 0 0 0-3.22-1.18c-.97 0-1.94.32-1.94 1.21 0 .9 1.04 1.21 2.24 1.66 2.1.7 3.84 1.58 3.84 3.64 0 2.24-1.74 3.78-4.58 3.95l-.26 1.2a.49.49 0 0 1-.48.39H9.63l-.09-.01a.5.5 0 0 1-.38-.59l.28-1.27a6.54 6.54 0 0 1-2.88-1.57v-.01a.48.48 0 0 1 0-.68l1-.97a.49.49 0 0 1 .67 0c.91.86 2.13 1.34 3.39 1.32 1.3 0 2.17-.55 2.17-1.42 0-.87-.88-1.1-2.54-1.72-1.76-.63-3.43-1.52-3.43-3.6 0-2.42 2.01-3.6 4.39-3.71L12.46 5a.48.48 0 0 1 .48-.38h1.78l.1.01c.26.06.43.32.37.59l-.31 1.39c.95.32 1.84.83 2.59 1.5l.02.02a.49.49 0 0 1-.07.69z" />
    </svg>
  );
}

// Buyer-facing payment methods a merchant can enable by default. These
// are the only methods surfaced on the hosted checkout, so the dashboard
// selector mirrors them exactly (no lightning-btc / card-bank / peer).
const ALL_METHODS: {
  id: string;
  label: string;
  sub: string;
  icon: React.ReactNode;
  iconBg: string;
  // `desktopOnly` flags methods that depend on a browser extension and
  // therefore can't run on mobile devices. The tile renders an amber
  // info-strip explaining the constraint so a merchant doesn't enable
  // it expecting full coverage.
  desktopOnly?: boolean;
  // Amount range the method actually accepts. Enforced when the buyer
  // picks the method on the hosted page, NOT at session creation — a
  // session outside the range is created fine and then can't be paid
  // with that method, so merchants need to see it while choosing.
  limits?: string;
}[] = [
  {
    id: "coinbase",
    // Lead with what the buyer does (cards / bank / Apple Pay) rather
    // than the brand — most buyers don't have a Coinbase relationship,
    // they just want to pay with what they already use. Attribution
    // ("Powered by Coinbase") moves to the subtitle / chips below.
    // Matches the CheckoutPage and AddFundsPage labelling so the
    // dashboard, hosted checkout, and add-funds flow are coherent.
    label: "Card, Bank or Apple Pay",
    sub: "Powered by Coinbase",
    icon: <CoinbaseGlyph className="w-5 h-5" />,
    iconBg: "linear-gradient(135deg, #0052FF, #0040CC)",
    limits: "$5 – $10,000",
  },
  {
    id: "wallet",
    label: "Wallet Transfer (USDC)",
    sub: "Send USDC on Base",
    icon: <Wallet className="w-4 h-4 text-black" strokeWidth={2.4} />,
    iconBg: "linear-gradient(135deg, #61CE70, #50B060)",
    limits: "$0.50 – $100,000",
  },
  {
    id: "cashapp",
    label: "CashApp Pay",
    sub: "Instant via Lightning",
    icon: <CashAppGlyph className="w-6 h-6 rounded-md bg-white text-[#00D632]" />,
    iconBg: "rgba(0,214,50,0.12)",
    limits: "$1 – ~$16,000",
  },
  {
    id: "peer",
    // "Peer Extension" — uses the CashTap browser extension to bridge
    // CashApp / Venmo / other consumer wallets directly to USDC on
    // Base, P2P-style. Mirrors the "Wallet Apps" tile in AddFundsPage
    // and PaymentInvoicePage so merchants enabling it know exactly
    // what their buyers will see.
    label: "Peer Extension",
    sub: "Pay from CashApp, Venmo, etc.",
    icon: <Smartphone className="w-4.5 h-4.5 text-white" strokeWidth={2.2} />,
    iconBg: "linear-gradient(135deg, #8B5CF6, #6D28D9)",
    desktopOnly: true,
  },
];

export default function ApiSettingsPage() {
  const user = useSelector((s: RootState) => (s as any).user) as any;
  const wallet: string | null = user?.wallet || user?.smartWallet || null;
  const email: string | null = user?.email || null;

  // Source `isBusiness` directly from Firestore via a live snapshot.
  //
  // Why not use the Redux mirror: on a hard refresh the Redux store
  // hydrates AFTER the component first renders, so `user.isBusiness`
  // briefly reads `false` for a real Business user — long enough to
  // flash the "Business accounts only" gate before the true value
  // arrives. Subscribing here is the same pattern Layout.tsx uses for
  // the Developers nav entry, so the two surfaces agree on the user's
  // status on every render path (refresh, navigation, real-time
  // upgrade from non-business → business).
  //
  // `businessStatus` starts as "unknown" so we can show a spinner
  // instead of either the gate OR the merchant panel while the
  // snapshot is in-flight.
  const [businessStatus, setBusinessStatus] = useState<
    "unknown" | "business" | "non-business"
  >("unknown");
  useEffect(() => {
    if (!wallet) {
      // No wallet yet → can't read the user doc. Stay "unknown" so the
      // page shows a spinner rather than flashing the non-business
      // gate while auth finishes hydrating.
      setBusinessStatus("unknown");
      return;
    }
    const unsub = onSnapshot(
      doc(db, "users", wallet),
      (snap) => {
        if (snap.exists() && snap.data().isBusiness) {
          setBusinessStatus("business");
        } else {
          setBusinessStatus("non-business");
        }
      },
      () => {
        // On a Firestore read failure assume non-business — we'd
        // rather show the gate (which is just an "apply" form) than
        // crash. The Redux mirror is the secondary signal.
        setBusinessStatus(user?.isBusiness ? "business" : "non-business");
      },
    );
    return () => unsub();
  }, [wallet, user?.isBusiness]);
  const isBusiness = businessStatus === "business";

  const [state, setState] = useState<MyState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!wallet) {
      setError("Connect your wallet to continue.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const s = await fetchMyMerchant(wallet, email);
      setState(s);
    } catch (e: any) {
      setError(e.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [wallet, email]);

  useEffect(() => {
    load();
  }, [load]);

  // Optimistic patch helpers — used by the approved panel so individual
  // edits don't trigger a full-page reload (and the spinner that came
  // with it). The merchant state is updated locally; the server is the
  // source of truth on the next load.
  const patchMerchant = useCallback((updater: (m: Merchant) => Merchant) => {
    setState((cur) => {
      if (cur?.state !== "approved") return cur;
      return { ...cur, merchant: updater(cur.merchant) };
    });
  }, []);

  const patchKeys = useCallback(
    (updater: (keys: ApiKeySummary[]) => ApiKeySummary[]) => {
      setState((cur) => {
        if (cur?.state !== "approved") return cur;
        return { ...cur, keys: updater(cur.keys) };
      });
    },
    [],
  );

  // While we don't yet know whether the user is a Business account —
  // typically the brief window after a hard refresh while Firestore
  // resolves — show a neutral spinner. Without this gate, a real
  // Business user would briefly see the "Business accounts only"
  // panel and have it swap out under them, which reads as a bug.
  if (businessStatus === "unknown") {
    return (
      <div className="max-w-3xl mx-auto py-24 px-4 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-6 h-6 text-[#61CE70] animate-spin" />
        <p className="text-gray-500 text-sm">Loading your account…</p>
      </div>
    );
  }

  if (!isBusiness) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <Toaster position="top-center" />
        <NonBusinessNotice />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-8 sm:py-10 px-4">
        <PageHeader />
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <Toaster position="top-center" />
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-red-200 font-semibold">Couldn't load Developers</p>
            <p className="text-red-300/80 text-sm mt-1">{error}</p>
            <button
              onClick={load}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-200 hover:text-white"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-10 px-4">
      <Toaster position="top-center" />
      <PageHeader />

      {state?.state === "none" || state?.state === "rejected" ? (
        <RequestAccessForm
          wallet={wallet}
          email={email}
          previousRequest={state?.state === "rejected" ? state.request : null}
          onSubmitted={load}
        />
      ) : state?.state === "pending" ? (
        <PendingStatus request={state.request} onRefresh={load} />
      ) : state?.state === "approved" ? (
        <ApprovedPanel
          merchant={state.merchant}
          keys={state.keys}
          wallet={wallet}
          email={email}
          patchMerchant={patchMerchant}
          patchKeys={patchKeys}
        />
      ) : null}
    </div>
  );
}

// ── Page Header ────────────────────────────────────────────────────────────
function PageHeader() {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#61CE70]/20 via-[#50B060]/10 to-transparent border border-[#61CE70]/30 flex items-center justify-center">
          <Code2 className="w-5 h-5 text-[#61CE70]" />
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#61CE70] font-semibold">CashTap Developers</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">API Settings</h1>
        </div>
      </div>
      <p className="mt-3 text-gray-400 text-sm max-w-2xl">
        Accept payments programmatically from your site or app. Create checkout sessions, brand the payment page, and settle directly into your CashTap wallet.
      </p>
    </div>
  );
}

function NonBusinessNotice() {
  return (
    <>
      <PageHeader />
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-100 font-semibold">Business accounts only</p>
            <p className="text-amber-200/80 text-sm mt-1 leading-relaxed">
              The Checkout API is available to verified business accounts. Upgrade your CashTap profile to a business account from Settings to apply.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Request Access Form ──────────────────────────────────────────────────
function RequestAccessForm({
  wallet,
  email,
  previousRequest,
  onSubmitted,
}: {
  wallet: string | null;
  email: string | null;
  previousRequest: any;
  onSubmitted: () => void;
}) {
  const [businessName, setBusinessName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [useCase, setUseCase] = useState("");
  const [volume, setVolume] = useState<string>("");
  const [domains, setDomains] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!businessName.trim() || !websiteUrl.trim() || !useCase.trim()) {
      toast.error("Business name, website, and use case are required.");
      return;
    }
    setSubmitting(true);
    try {
      await requestApiAccess(wallet, email, {
        businessName: businessName.trim(),
        websiteUrl: websiteUrl.trim(),
        useCase: useCase.trim(),
        monthlyVolumeUsd: volume ? Number(volume) : null,
        redirectDomains: domains,
      });
      toast.success("Request submitted. Our team will review shortly.");
      onSubmitted();
    } catch (e: any) {
      toast.error(e.message || "Failed to submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/80 to-[#0B0F16]/80 p-6 sm:p-8">
      {previousRequest && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/5 border border-red-500/25 flex items-start gap-3">
          <XIcon className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-red-200 text-sm font-semibold">Previous request rejected</p>
            {previousRequest.reviewerNote && (
              <p className="text-red-300/80 text-sm mt-1 leading-relaxed whitespace-pre-wrap">
                {previousRequest.reviewerNote}
              </p>
            )}
            <p className="text-red-300/60 text-xs mt-1.5">You can re-apply with updated information below.</p>
          </div>
        </div>
      )}

      <h2 className="text-lg font-bold text-white">Request API access</h2>
      <p className="text-gray-400 text-sm mt-1">
        Manual review, usually within 1 business day. You'll get an email once we decide.
      </p>

      <div className="mt-6 space-y-4">
        <FormField label="Business name *">
          <input
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Acme Inc."
            className={inputCls}
          />
        </FormField>
        <FormField label="Website URL *">
          <input
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://acme.com"
            className={inputCls}
          />
        </FormField>
        <FormField label="What will you build with the Checkout API? *">
          <textarea
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            placeholder="e.g. accept payments on our SaaS pricing page; one-time digital good purchases."
            rows={4}
            className={`${inputCls} resize-none`}
          />
        </FormField>
        <FormField label="Estimated monthly volume (USD)">
          <input
            type="number"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            placeholder="5000"
            className={inputCls}
          />
        </FormField>
        <FormField label="Redirect domains (where buyers will land after payment)">
          <DomainChipInput value={domains} onChange={setDomains} />
        </FormField>
      </div>

      <button
        onClick={submit}
        disabled={submitting}
        className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#61CE70] hover:bg-[#50B060] disabled:opacity-60 text-black text-sm font-semibold transition-colors"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {submitting ? "Submitting…" : "Submit request"}
      </button>
    </div>
  );
}

// ── Pending Status ────────────────────────────────────────────────────────
function PendingStatus({ request, onRefresh }: { request: any; onRefresh: () => void }) {
  return (
    <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/5 to-transparent p-6 sm:p-7">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center shrink-0">
          <Clock className="w-5 h-5 text-amber-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-amber-100 font-semibold">Request under review</p>
          <p className="text-amber-200/80 text-sm mt-1 leading-relaxed">
            Your request for <span className="text-white font-medium">{request.businessName}</span> is being reviewed by our team. We'll email you as soon as a decision is made.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="rounded-xl bg-black/20 border border-amber-500/15 px-3 py-2">
              <span className="text-amber-200/60">Website</span>
              <p className="text-white font-mono text-[12px] truncate mt-0.5">{request.websiteUrl}</p>
            </div>
            <div className="rounded-xl bg-black/20 border border-amber-500/15 px-3 py-2">
              <span className="text-amber-200/60">Request ID</span>
              <p className="text-white font-mono text-[12px] truncate mt-0.5">{request.id}</p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-white"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Check status
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approved Panel ────────────────────────────────────────────────────────
function ApprovedPanel({
  merchant,
  keys,
  wallet,
  email,
  patchMerchant,
  patchKeys,
}: {
  merchant: Merchant;
  keys: ApiKeySummary[];
  wallet: string | null;
  email: string | null;
  patchMerchant: (updater: (m: Merchant) => Merchant) => void;
  patchKeys: (updater: (keys: ApiKeySummary[]) => ApiKeySummary[]) => void;
}) {
  return (
    <div className="space-y-6">
      <ApprovedBanner merchant={merchant} />
      <KeysPanel keys={keys} wallet={wallet} email={email} patchKeys={patchKeys} />
      <BrandingPanel merchant={merchant} wallet={wallet} email={email} patchMerchant={patchMerchant} />
      <DomainsAndMethodsPanel merchant={merchant} wallet={wallet} email={email} patchMerchant={patchMerchant} />
      <WebhookSummaryCard wallet={wallet} email={email} />
    </div>
  );
}

function ApprovedBanner({ merchant }: { merchant: Merchant }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-2xl border border-[#61CE70]/30 bg-gradient-to-br from-[#61CE70]/10 via-transparent to-transparent p-5 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-[#61CE70]/15 border border-[#61CE70]/30 flex items-center justify-center shrink-0">
        <ShieldCheck className="w-5 h-5 text-[#61CE70]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-white font-semibold leading-tight truncate">{merchant.businessName}</p>
          {/* Compact active dot — keeps account status visible on mobile,
              where the full pill below is hidden to save width. */}
          <span
            className="sm:hidden w-2 h-2 rounded-full bg-[#61CE70] shrink-0"
            title="Active"
            aria-label="Active"
          />
        </div>
        <p className="text-gray-400 text-xs truncate">
          Merchant ID · <span className="font-mono text-gray-300">{merchant.id}</span>
        </p>
      </div>
      {/* Copy is a primary affordance here — keep it on every breakpoint with
          a 44px touch target. */}
      <button
        onClick={() => {
          navigator.clipboard.writeText(merchant.id);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="shrink-0 p-2.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
        title="Copy merchant ID"
        aria-label="Copy merchant ID"
      >
        {copied ? <Check className="w-4 h-4 text-[#61CE70]" /> : <Copy className="w-4 h-4" />}
      </button>
      <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#61CE70] px-2.5 py-1 rounded-full border border-[#61CE70]/30 bg-[#61CE70]/10">
        <span className="w-1.5 h-1.5 rounded-full bg-[#61CE70]" />
        Active
      </span>
    </div>
  );
}

// ─── Keys ──────────────────────────────────────────────────────────────────
function KeysPanel({
  keys,
  wallet,
  email,
  patchKeys,
}: {
  keys: ApiKeySummary[];
  wallet: string | null;
  email: string | null;
  patchKeys: (updater: (keys: ApiKeySummary[]) => ApiKeySummary[]) => void;
}) {
  const [revealedKey, setRevealedKey] = useState<{ raw: string; prefix: string; label: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKeySummary | null>(null);
  const [revoking, setRevoking] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      const res = await rotateApiKey(wallet, email);
      setRevealedKey({ raw: res.key.raw, prefix: res.key.prefix, label: (res.key as any).label || "" });
      // Optimistic insert — show the new key at the top of the list
      // immediately so the user sees confirmation without a full reload.
      patchKeys((cur) => [
        {
          id: res.key.id,
          prefix: res.key.prefix,
          label: (res.key as any).label || `Key #${cur.filter((k) => !k.revokedAt).length + 1}`,
          createdAt: new Date().toISOString(),
          lastUsedAt: null,
          revokedAt: null,
        },
        ...cur,
      ]);
      setConfirmCreate(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to create key.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      await revokeApiKey(wallet, email, confirmRevoke.id);
      patchKeys((cur) =>
        cur.map((k) =>
          k.id === confirmRevoke.id ? { ...k, revokedAt: new Date().toISOString() } : k,
        ),
      );
      toast.success("Key revoked.");
      setConfirmRevoke(null);
    } catch (e: any) {
      toast.error(e.message || "Failed to revoke key.");
    } finally {
      setRevoking(false);
    }
  };

  const activeKeys = useMemo(() => keys.filter((k) => !k.revokedAt), [keys]);

  return (
    <>
      <Section
        icon={<Key className="w-4 h-4 text-[#61CE70]" />}
        title="API keys"
        desc="Live keys for production traffic. Treat them like passwords and never commit them to source control."
        action={
          <button
            onClick={() => setConfirmCreate(true)}
            disabled={creating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#61CE70]/10 hover:bg-[#61CE70]/15 text-[#61CE70] text-xs font-semibold border border-[#61CE70]/25 transition-colors disabled:opacity-60"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            New key
          </button>
        }
      >
        {revealedKey && <RevealedKeyCard data={revealedKey} onDismiss={() => setRevealedKey(null)} />}

        {activeKeys.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-700/60 bg-gray-900/20 p-6 text-center">
            <Key className="w-5 h-5 mx-auto text-gray-600 mb-2" />
            <p className="text-gray-400 text-sm">No active keys yet.</p>
            <p className="text-gray-600 text-xs mt-1">Click "New key" above to mint your first one.</p>
          </div>
        ) : (
          <>
            {/* Security-model notice — keys are hashed at rest, so the
                raw value can never be re-displayed. Users sometimes
                expect a copy button on the prefix; instead we explain
                what to do if they've lost a key. */}
            <div className="mb-3 flex items-start gap-2.5 p-3 rounded-xl border border-gray-800/80 bg-gray-900/30">
              <Lock className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-0.5" />
              <p className="text-[12px] text-gray-400 leading-relaxed">
                Full API keys are only shown <span className="text-white font-semibold">once, when you create them</span>. The prefixes below are just labels for identification, not the actual key. If you've lost a key, click <span className="text-[#61CE70] font-medium">New key</span> and revoke the old one after your code switches over.
              </p>
            </div>
            <ul className="space-y-2">
              {activeKeys.map((k) => (
                <KeyRow key={k.id} apiKey={k} onRevoke={() => setConfirmRevoke(k)} />
              ))}
            </ul>
          </>
        )}
      </Section>

      <ConfirmModal
        open={confirmCreate}
        onClose={() => !creating && setConfirmCreate(false)}
        onConfirm={create}
        busy={creating}
        title="Generate a new API key?"
        description="You'll see the new key only once, so copy it and store it somewhere safe before closing this window. Your current keys keep working until you revoke them."
        confirmLabel="Generate key"
        icon={<Sparkles className="w-5 h-5" />}
      />

      <ConfirmModal
        open={!!confirmRevoke}
        onClose={() => !revoking && setConfirmRevoke(null)}
        onConfirm={revoke}
        busy={revoking}
        variant="danger"
        title="Revoke this key?"
        description={
          confirmRevoke
            ? `Anything still using "${confirmRevoke.label}" (${confirmRevoke.prefix}……) will stop working right away. This can't be undone.`
            : ""
        }
        confirmLabel="Revoke key"
        icon={<Trash2 className="w-5 h-5" />}
      />
    </>
  );
}

function KeyRow({ apiKey, onRevoke }: { apiKey: ApiKeySummary; onRevoke: () => void }) {
  const display = `${apiKey.prefix}……`;
  const created =
    apiKey.createdAt && (apiKey.createdAt as any).toDate
      ? (apiKey.createdAt as any).toDate().toLocaleDateString()
      : typeof apiKey.createdAt === "string"
        ? new Date(apiKey.createdAt).toLocaleDateString()
        : null;
  return (
    <li className="flex items-center gap-3 p-3 rounded-xl bg-black/20 border border-gray-800/60 hover:border-gray-700 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center shrink-0">
        <Key className="w-4 h-4 text-[#61CE70]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-white text-sm font-medium truncate">{apiKey.label}</p>
          {created && (
            <span className="text-[10px] text-gray-500 shrink-0">· created {created}</span>
          )}
        </div>
        <p className="text-[#61CE70]/80 text-xs font-mono mt-0.5">{display}</p>
      </div>
      <button
        onClick={onRevoke}
        className="shrink-0 p-2 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors"
        title="Revoke key"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

function RevealedKeyCard({
  data,
  onDismiss,
}: {
  data: { raw: string; prefix: string; label: string };
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mb-4 p-4 rounded-xl border border-[#61CE70]/30 bg-[#61CE70]/5">
      <div className="flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-[#61CE70] shrink-0 mt-1" />
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold text-sm">{data.label} is ready. Save it now.</p>
          <p className="text-gray-400 text-xs mt-0.5">
            This is the only time you'll see this key. If you lose it, you'll need to create a new one and revoke this.
          </p>
          <div className="mt-3 flex items-center gap-2 p-2.5 rounded-lg bg-black/30 border border-gray-800">
            <code className="text-[#61CE70] font-mono text-xs sm:text-sm break-all flex-1 min-w-0">
              {data.raw}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(data.raw);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="shrink-0 p-2 rounded-lg hover:bg-white/5"
            >
              {copied ? <Check className="w-4 h-4 text-[#61CE70]" /> : <Copy className="w-4 h-4 text-gray-400" />}
            </button>
          </div>
          <button onClick={onDismiss} className="mt-3 text-xs text-gray-400 hover:text-white">
            I've copied it
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Branding ──────────────────────────────────────────────────────────────
function BrandingPanel({
  merchant,
  wallet,
  email,
  patchMerchant,
}: {
  merchant: Merchant;
  wallet: string | null;
  email: string | null;
  patchMerchant: (updater: (m: Merchant) => Merchant) => void;
}) {
  const [logoUrl, setLogoUrl] = useState(merchant.branding.logoUrl || "");
  const [brandColor, setBrandColor] = useState(merchant.branding.brandColor || "#61CE70");
  const [accentColor, setAccentColor] = useState(merchant.branding.accentColor || "#50B060");
  const [footerText, setFooterText] = useState(merchant.branding.footerText || "");
  const [businessNameDisplay, setBusinessNameDisplay] = useState(
    merchant.branding.businessNameDisplay || merchant.businessName,
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const next = {
        logoUrl: logoUrl || null,
        brandColor,
        accentColor,
        footerText: footerText || null,
        businessNameDisplay,
      };
      await updateMerchantSettings(wallet, email, { branding: next });
      patchMerchant((m) => ({ ...m, branding: next }));
      toast.success("Branding saved.");
    } catch (e: any) {
      toast.error(e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section
      icon={<Palette className="w-4 h-4 text-[#61CE70]" />}
      title="Branding"
      desc="Customize how your checkout page looks. Buyers see this on pay.cashtap.cash."
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8 items-start">
        {/* ── FORM ──────────────────────────────────────────────── */}
        <div className="space-y-5">
          {/* Identity group */}
          <SubGroup
            heading="Identity"
            description="The name and logo buyers see on the checkout page."
          >
            <FormField label="Display name">
              <input
                value={businessNameDisplay}
                onChange={(e) => setBusinessNameDisplay(e.target.value)}
                placeholder="Acme Inc."
                className={inputCls}
              />
            </FormField>
            <FormField label="Logo URL">
              <LogoInput value={logoUrl} onChange={setLogoUrl} />
            </FormField>
          </SubGroup>

          {/* Colors group */}
          <SubGroup
            heading="Colors"
            description="Used for the Pay button, accents, and highlights."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ColorField
                label="Brand color"
                value={brandColor}
                onChange={setBrandColor}
                description="Primary CTA + key accents"
              />
              <ColorField
                label="Accent color"
                value={accentColor}
                onChange={setAccentColor}
                description="Secondary highlights + gradient"
              />
            </div>
            {/* Quick gradient preview so the chosen pair is immediately legible */}
            <div className="rounded-xl p-px overflow-hidden">
              <div
                className="rounded-[11px] py-2 px-3 text-center text-[11px] font-bold text-black"
                style={{
                  background: `linear-gradient(135deg, ${brandColor}, ${accentColor})`,
                }}
              >
                Gradient preview
              </div>
            </div>
          </SubGroup>

          {/* Footer text */}
          <SubGroup
            heading="Footer text"
            description="Optional line below the Pay button. Good for support contact."
          >
            <FormField label="Footer text">
              <input
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                placeholder="Need help? support@acme.com"
                className={inputCls}
              />
            </FormField>
          </SubGroup>

          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#61CE70] hover:bg-[#50B060] disabled:opacity-60 text-black text-sm font-semibold transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save branding
          </button>
        </div>

        {/* ── LIVE PREVIEW (sticky on desktop) ───────────────────── */}
        <div className="lg:sticky lg:top-20">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold">
              Live preview
            </p>
            <span className="text-[10px] text-gray-600">Updates as you type</span>
          </div>
          <BrandingPreview
            displayName={businessNameDisplay}
            logoUrl={logoUrl}
            brandColor={brandColor}
            accentColor={accentColor}
            footerText={footerText}
            methods={merchant.allowedPaymentMethods || []}
          />
        </div>
      </div>
    </Section>
  );
}

// Small group wrapper used inside Branding to group related fields with
// a heading and short description. Keeps the form scannable.
function SubGroup({
  heading,
  description,
  children,
}: {
  heading: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-gray-900/30 border border-gray-800/60 p-4 space-y-3">
      <div>
        <p className="text-white text-sm font-semibold">{heading}</p>
        {description && (
          <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

// Logo URL input with a live thumbnail next to the field. Falls back to
// a placeholder block if the URL is empty or fails to load.
function LogoInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [ok, setOk] = useState<boolean | null>(null);
  useEffect(() => {
    if (!value) {
      setOk(null);
      return;
    }
    setOk(null);
    const img = new Image();
    img.onload = () => setOk(true);
    img.onerror = () => setOk(false);
    img.src = value;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [value]);

  const hasLogo = !!value && ok === true;
  return (
    <div className="flex items-stretch gap-2">
      {/* White tile when a logo is present — most marks are drawn for light
          backgrounds and vanish on dark. Falls back to a dark placeholder
          while empty/broken so an empty white box doesn't glare. */}
      <div
        className={`shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center overflow-hidden transition-colors ${
          hasLogo ? "bg-white border-gray-300 p-1" : "bg-black/30 border-gray-800"
        }`}
      >
        {hasLogo ? (
          <img src={value} alt="Logo" className="max-w-full max-h-full object-contain" />
        ) : (
          <ImageIcon
            className={`w-4 h-4 ${value && ok === false ? "text-amber-400" : "text-gray-600"}`}
          />
        )}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://yoursite.com/logo.png"
        className={inputCls}
      />
    </div>
  );
}

// Color picker + hex input + a tiny preview tile of the live color.
// More visually obvious than the previous flat input row.
function ColorField({
  label,
  value,
  onChange,
  description,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  description?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-[0.16em] text-gray-500 font-semibold">
          {label}
        </span>
      </div>
      <div className="flex items-stretch gap-2">
        {/* Native color picker styled to look like a swatch tile */}
        <label
          className="relative w-11 h-11 rounded-xl border border-gray-800 cursor-pointer overflow-hidden shrink-0"
          style={{ background: value }}
        >
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            aria-label={`${label} color picker`}
          />
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} font-mono uppercase`}
        />
      </div>
      {description && (
        <p className="text-[10px] text-gray-600 mt-1.5 leading-snug">{description}</p>
      )}
    </div>
  );
}

// ─── Domains & Methods ─────────────────────────────────────────────────────
function DomainsAndMethodsPanel({
  merchant,
  wallet,
  email,
  patchMerchant,
}: {
  merchant: Merchant;
  wallet: string | null;
  email: string | null;
  patchMerchant: (updater: (m: Merchant) => Merchant) => void;
}) {
  const [domains, setDomains] = useState<string[]>(merchant.allowedRedirectDomains || []);
  const [methods, setMethods] = useState<string[]>(merchant.allowedPaymentMethods || []);
  const [saving, setSaving] = useState(false);

  const toggle = (id: string) => {
    setMethods((cur) => (cur.includes(id) ? cur.filter((m) => m !== id) : [...cur, id]));
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateMerchantSettings(wallet, email, {
        allowedRedirectDomains: domains,
        allowedPaymentMethods: methods,
      });
      patchMerchant((m) => ({ ...m, allowedRedirectDomains: domains, allowedPaymentMethods: methods }));
      toast.success("Settings saved.");
    } catch (e: any) {
      toast.error(e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section
      icon={<Code2 className="w-4 h-4 text-[#61CE70]" />}
      title="Allowed domains & payment methods"
      desc="Domains are validated against your success_url / cancel_url. Methods can be further narrowed per checkout session via the API."
    >
      <FormField label="Allowed redirect domains">
        <DomainChipInput value={domains} onChange={setDomains} />
      </FormField>
      <div className="mt-6">
        <label className="block text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold mb-1.5">
          Default payment methods
        </label>
        {/* Merchants read these tiles as the API contract, so spell the
            contract out: the id on each tile is the literal value for
            `payment_methods`, and a session can only narrow this list —
            asking for a method that isn't enabled here is an error, not a
            silent drop. */}
        <p className="text-gray-500 text-xs leading-relaxed mb-3">
          The API id on each tile is the value the Checkout API accepts in{" "}
          <code className="px-1.5 py-0.5 rounded-md bg-gray-800/60 text-[#61CE70] font-mono text-[11px]">
            payment_methods
          </code>
          . A session can only narrow this list — requesting a method you haven't enabled here returns an error.
        </p>
        {/* Stacked single column so every tile gets the full panel
            width — labels, chips, and the "Powered by" badge all read
            at a comfortable size instead of being crammed into a
            half-width tile. Vertical spacing replaces the previous
            2-col grid gap. */}
        <div className="space-y-3">
          {/* `peer` (Peer Extension) is temporarily hidden from the selector
              while the peer extension is down and pending re-implementation.
              Remove "peer" from this list to restore it. */}
          {ALL_METHODS.filter((m) => !["peer"].includes(m.id)).map((m) => {
            const active = methods.includes(m.id);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => toggle(m.id)}
                aria-pressed={active}
                className={`group w-full flex items-center gap-4 sm:gap-5 px-4 sm:px-5 py-4 sm:py-5 rounded-2xl border text-left transition-colors ${
                  active
                    ? "bg-[#61CE70]/10 border-[#61CE70]/40 text-white"
                    : "bg-gray-900/30 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                <span
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
                  style={{ background: m.iconBg }}
                >
                  {m.icon}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-base sm:text-lg leading-tight text-white">
                    {m.label}
                  </span>
                  {/* The id this tile maps to in the Checkout API. A merchant
                      enabled "Card, Bank or Apple Pay", guessed `card-bank`
                      for payment_methods, and got a session back without it —
                      the label and the id don't match, so print the id the
                      API actually expects right where the method is chosen. */}
                  <span className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500 font-semibold shrink-0">
                      API id
                    </span>
                    <code className="px-1.5 py-0.5 rounded-md bg-gray-800/60 text-[#61CE70] font-mono text-[11px] leading-none truncate">
                      {m.id}
                    </code>
                    {m.limits && (
                      <>
                        <span className="text-gray-700 shrink-0" aria-hidden="true">
                          ·
                        </span>
                        <span className="text-[11px] text-gray-500 whitespace-nowrap">
                          {m.limits}
                        </span>
                      </>
                    )}
                  </span>
                  {/* Coinbase keeps the chips + "Powered by" badge
                      treatment from the AddFundsPage / CheckoutPage;
                      every other tile uses a single-line subtitle. */}
                  {m.id === "coinbase" ? (
                    <span className="block mt-2 space-y-2">
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-2 py-1 rounded-md bg-white/10 border border-white/10 text-white text-xs font-medium leading-none">
                          Debit Card
                        </span>
                        <span className="px-2 py-1 rounded-md bg-white inline-flex items-center justify-center leading-none">
                          <img
                            src="/applepay-logo.png"
                            alt="Apple Pay"
                            className="h-4 w-auto object-contain"
                          />
                        </span>
                        <span className="px-2 py-1 rounded-md bg-white/10 border border-white/10 text-white text-xs font-medium leading-none">
                          Bank
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500 leading-none">
                          Powered by
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/10 border border-white/10">
                          <img
                            src="/coinbase-small.jpg"
                            alt=""
                            aria-hidden="true"
                            className="h-4 w-4 rounded-full object-contain"
                          />
                          <span className="text-[#4287FF] font-bold text-xs leading-none">
                            Coinbase
                          </span>
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span className="block text-sm text-gray-500 leading-snug mt-1">
                      {m.sub}
                    </span>
                  )}
                  {/* Desktop-only methods (Peer Extension today) flag
                      the constraint right on the tile so the merchant
                      isn't surprised when mobile buyers can't see it
                      on the hosted checkout. */}
                  {m.desktopOnly && (
                    <span className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/25 text-amber-300 text-[11px] font-medium leading-none">
                      <AlertCircle className="w-3 h-3" />
                      Desktop browser only — not available on mobile devices
                    </span>
                  )}
                </span>
                <span
                  className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                    active ? "bg-[#61CE70] border-[#61CE70]" : "border-gray-600"
                  }`}
                >
                  {active && <Check className="w-4 h-4 text-black" strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#61CE70] hover:bg-[#50B060] disabled:opacity-60 text-black text-sm font-semibold transition-colors"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save settings
      </button>
    </Section>
  );
}

// Loading placeholder that mirrors the approved-panel layout (banner + a
// couple of section cards) so the page keeps its shape while data loads —
// less jarring than a centered spinner and avoids a layout jump on arrival.
function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-hidden="true">
      <div className="h-20 rounded-2xl border border-gray-800/80 bg-gray-900/30" />
      {[0, 1].map((i) => (
        <div key={i} className="rounded-2xl border border-gray-800/80 bg-gray-900/20 p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-gray-800/60 shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-40 rounded bg-gray-800/70" />
              <div className="h-2.5 w-64 max-w-full rounded bg-gray-800/40" />
            </div>
          </div>
          <div className="space-y-2.5">
            <div className="h-11 rounded-xl bg-gray-800/40" />
            <div className="h-11 rounded-xl bg-gray-800/30" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tiny shared primitives ────────────────────────────────────────────────
const inputCls =
  "w-full px-3 py-2.5 rounded-xl bg-gray-900/40 border border-gray-800 focus:border-[#61CE70]/50 focus:outline-none text-white text-sm placeholder-gray-600";

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-gray-500 font-semibold mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function Section({
  icon,
  title,
  desc,
  action,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  desc?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/70 to-[#0B0F16]/70 p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold">{title}</p>
          {desc && <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{desc}</p>}
        </div>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}
